import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Package, MessageCircle } from "lucide-react";
import { rgApi, EXTRA_LABELS, EXTRA_KEYS, EXTRA_ICONS, fmtLongDate } from "@/lib/rgApi";
import ModuleModal from "./ModuleModal";
import ShoppingListModal from "./ShoppingListModal";
import { moduleProgress } from "./rgModules";
import { parseBreakfastForm } from "./rgBreakfastParser";
import TimelineConversation from "./TimelineConversation";
import CommButtons from "./CommButtons";
import FormActionsBlock from "./FormActionsBlock";
import BreakfastBlock from "./BreakfastBlock";
import FicheSummary from "./FicheSummary";

/**
 * GuestDetail — Fiche voyageur, espace principal d'action sur un séjour.
 *   Bloc 1  Identité + icônes séjour + source lisible + boutons Comm + ajouts manuels
 *   Bloc 2  Actions / formulaires (couchage · petit-déj · bain fleuri) — AUCUN envoi auto
 *   Bloc 3  Conversation + À traiter + Extras + Note voyageur (+ notes Lodgify)
 *   Bloc 4  Préparation (petit-déj reçu + liste de courses)
 *
 * Source commune : réservation ↔ calendrier ↔ Aujourd'hui. Un extra ou un 3e
 * voyageur ajouté ici alimente icônes, actions et préparation partout.
 */

// Libellé source lisible (remplace le badge brut type "OH")
// Les réservations transitent toutes par Lodgify ; on distingue le canal d'origine.
function sourceLabel(source) {
  const s = (source || "").toLowerCase().trim();
  if (s.includes("airbnb")) return "Airbnb";
  if (s.includes("booking")) return "Booking.com";
  // "OH", "manual", "direct", "csv", vide… = réservation Lodgify (directe)
  return "Lodgify";
}

export default function GuestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const showArrival = searchParams.get("arrivee") === "1";
  const [r, setR] = useState(null);
  const [tpl, setTpl] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [moduleOpen, setModuleOpen] = useState(null);
  const [shoppingOpen, setShoppingOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [res, t] = await Promise.all([rgApi.getReservation(id), rgApi.getTemplates()]);
      setR(res); setTpl(t); setNotes(res.notes || "");
    } catch (e) {
      if (e.status === 401) nav("/admin");
    }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  // Scroll vers l'ancre (#petit-dejeuner, #bain-fleuri, #troisieme-lit) à l'ouverture
  useEffect(() => {
    if (!r) return;
    const hash = window.location.hash?.replace("#", "");
    if (!hash) return;
    const t = setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [r]);

  const patch = async (data) => {
    setBusy(true);
    try {
      const updated = await rgApi.updateReservation(id, data);
      setR(updated);
    } finally { setBusy(false); }
  };

  const toggleExtra = (key) => {
    const cur = r.extras || [];
    patch({ extras: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key] });
  };

  const saveNotes = () => patch({ notes });

  const deleteMessage = async (msgId) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    setBusy(true);
    try { const updated = await rgApi.deleteMessage(id, msgId); setR(updated); }
    finally { setBusy(false); }
  };

  const lodgifySync = async () => {    setBusy(true);
    try {
      const res = await rgApi.lodgifySyncMessages(r.id);
      if (res.ok) { await load(); alert(`${res.appended} nouveau(x) message(s) importé(s) depuis Lodgify.`); }
      else alert(`Sync impossible : ${res.error}`);
    } catch (e) { alert("Erreur : " + e.message); }
    finally { setBusy(false); }
  };

  /**
   * logAndSend — enregistre le message côté hub avant d'ouvrir le canal natif.
   */
  const logAndSend = async (channel, text, url) => {
    if (!text || !url) return;
    try {
      const updated = await rgApi.addMessage(id, { sender: "host", channel, text });
      setR(updated);
    } catch (e) { console.warn("[logAndSend] log failed:", e?.message); }
    if (channel === "sms") window.location.href = url;
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  // logMessage — enregistre un message envoyé (WhatsApp/SMS) SANS ouvrir de canal
  // (l'ouverture + la confirmation sont gérées par les blocs via sendUtils).
  const logMessage = async (channel, text) => {
    if (!text) return;
    try {
      const updated = await rgApi.addMessage(id, { sender: "host", channel, text });
      setR(updated);
    } catch (e) { console.warn("[logMessage] failed:", e?.message); }
  };

  if (!r) return <div className="rg-page rg-loading">Chargement…</div>;

  const extras = r.extras || [];
  const needsThirdGuest = (r.people || 0) >= 3 || !!r.extra_guest_added;
  const bf = r.breakfast_form || {};
  const breakfastReceived = bf.received === true || r.petit_dejeuner_confirmed === true || parseBreakfastForm(r.notes).ok;
  const effectivePeople = r.extra_guest_added && (r.people || 0) < 3 ? (r.people || 0) + 1 : (r.people || 0);

  // Icônes du séjour — mêmes emojis que le calendrier / Aujourd'hui
  const stayIcons = [];
  if (needsThirdGuest) stayIcons.push({ icon: "🛏️", pending: !r.troisieme_lit_type, label: "3e couchage" });
  if (extras.includes("petit_dejeuner")) stayIcons.push({ icon: "🥐", pending: !breakfastReceived, label: "Petit déjeuner" });
  if (extras.includes("bain_fleurs")) stayIcons.push({ icon: "🌸", pending: (r.form_status || {}).bain_fleurs !== "received", label: "Bain de fleurs" });
  if (extras.includes("decor")) stayIcons.push({ icon: "🌹", pending: false, label: "Décor du refuge" });

  // Modules de préparation actifs (bloc Préparation & courses, tout en bas)
  const MODULE_MAP = { petit_dejeuner: "petit_dej", bain_fleurs: "bain_fleurs", decor: "decor" };
  const activeModules = Object.keys(MODULE_MAP)
    .filter((k) => extras.includes(k))
    .map((k) => ({ key: k, modKey: MODULE_MAP[k] }));

  return (
    <div className="rg-page" data-testid="rg-guest-page">
      <button className="rg-btn ghost small" onClick={() => nav(-1)} style={{ marginBottom: 12 }}>
        <ChevronLeft size={14} /> Retour
      </button>

      {/* Bannière « Informations d'arrivée » — affichée quand ouverte depuis la notif 14h */}
      {showArrival && (
        <div className="rg-card" data-testid="rg-arrival-banner"
          style={{ marginBottom: 16, border: "2px solid var(--rg-primary)", background: "var(--rg-primary-light)" }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 16 }}>🔑 Informations d'arrivée à envoyer</h3>
          <p style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.5 }}>
            Ouvre la conversation WhatsApp de <b>{r.name}</b>, puis saisis ta réponse rapide
            <b> /1CODE</b>, vérifie le contenu et appuie sur Envoyer.
          </p>
          {(() => {
            const wa = (r.phone || "").replace(/[^\d+]/g, "").replace(/^\+?/, "");
            const digits = (r.phone || "").replace(/\D/g, "");
            const valid = digits.length >= 8 && digits.length <= 15;
            return valid ? (
              <a className="rg-btn" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
                data-testid="rg-arrival-open-whatsapp"
                style={{ background: "#25D366", borderColor: "#25D366", color: "#fff" }}>
                <MessageCircle size={14} /> Ouvrir WhatsApp
              </a>
            ) : (
              <p className="rg-tiny" style={{ color: "#8a6d1a", margin: 0 }} data-testid="rg-arrival-no-phone">
                Aucun numéro valide sur cette fiche — ajoute le numéro du voyageur pour ouvrir WhatsApp.
              </p>
            );
          })()}
        </div>
      )}

      {/* ====================== BLOC 1 — Identité, Séjour, Comm ====================== */}
      <div className="rg-card" style={{ marginBottom: 16 }} data-testid="rg-block-identity">
        <div className="rg-row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div className="rg-row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <h2 className="rg-h2" data-testid="rg-guest-name" style={{ margin: 0 }}>{r.name}</h2>
              {stayIcons.length > 0 && (
                <span className="rg-row" style={{ gap: 4 }} data-testid="rg-stay-icons">
                  {stayIcons.map((s, i) => (
                    <span key={i} title={s.label} data-testid={`rg-stay-icon-${s.icon}`} style={{ fontSize: 18, lineHeight: 1 }}>
                      {s.icon}{s.pending && <span style={{ color: "#c9820a", fontWeight: 700 }}>❓</span>}
                    </span>
                  ))}
                </span>
              )}
            </div>
            <div className="rg-muted" style={{ marginTop: 4 }}>
              {fmtLongDate(r.date_arrival)} → {fmtLongDate(r.date_departure)} · {r.nights} nuits · {effectivePeople} personne{effectivePeople > 1 ? "s" : ""}
              {r.extra_guest_added && (r.people || 0) < 3 && (
                <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}> (3e voyageur ajouté au Hub)</span>
              )}
            </div>
            <span className="rg-chip" style={{ marginTop: 6 }} data-testid="rg-source-label">{sourceLabel(r.source)}</span>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <CommButtons reservation={r} templates={tpl} onLogSend={logAndSend} showContact showTemplate={false} />
        </div>
      </div>

      {/* ====================== 2 — 🥐 Formulaire petit-déjeuner ====================== */}
      {extras.includes("petit_dejeuner") && (
        <BreakfastBlock
          reservation={r}
          templates={tpl}
          onPatch={patch}
          onLog={logMessage}
          busy={busy}
        />
      )}

      {/* ====================== 2b — Expériences : Bain fleuri · Couchage (blocs dédiés) ====================== */}
      <FormActionsBlock
        reservation={r}
        templates={tpl}
        onPatch={patch}
        onLog={logMessage}
        needsCouchage={needsThirdGuest}
        hasBain={extras.includes("bain_fleurs")}
        busy={busy}
      />

      {/* ====================== 3 — Résumé & notes (au-dessus des conversations) ====================== */}
      <div className="rg-section" data-testid="rg-block-summary-notes">
        <h3>Résumé &amp; notes</h3>
        {(r.notes_lodgify || "").trim() && (
          <div className="rg-card" data-testid="rg-notes-lodgify" style={{ background: "var(--rg-bg)", marginBottom: 10, padding: 12 }}>
            <p className="rg-tiny" style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--rg-text-2)" }}>
              📥 Note Lodgify (lecture seule)
            </p>
            <p style={{ margin: 0, fontSize: 13, whiteSpace: "pre-wrap" }}>{r.notes_lodgify}</p>
          </div>
        )}
        <div className="rg-card" style={{ padding: 12, marginBottom: 10 }}>
          <p className="rg-tiny" style={{ margin: "0 0 6px", fontWeight: 600, color: "var(--rg-text-2)" }}>Résumé</p>
          <FicheSummary reservation={r} />
        </div>
        <label className="rg-tiny" style={{ display: "block", marginBottom: 6, color: "var(--rg-text-2)", fontWeight: 600 }}>Note interne (manuelle · jamais écrasée par Lodgify)</label>
        <textarea className="rg-textarea" value={notes} onChange={(e) => setNotes(e.target.value)}
          data-testid="rg-notes"
          placeholder="Ta note interne : contexte, préférences, allergies, mots-clés (anniversaire, surprise, lune de miel…)" />
        <button className="rg-btn" style={{ marginTop: 8 }} onClick={saveNotes} disabled={busy} data-testid="rg-notes-save">
          Enregistrer la note
        </button>
      </div>

      {/* ====================== 4 — Conversations (Lodgify + WhatsApp Business) ====================== */}
      <TimelineConversation
        reservation={r}
        onDeleteMessage={deleteMessage}
        onLodgifySync={lodgifySync}
        busy={busy}
      />

      {/* ====================== 5 — Ajouter un extra ====================== */}
      <div className="rg-section" data-testid="rg-block-extras">
        <h3>+ Ajouter un extra après réservation</h3>
        <p className="rg-muted" style={{ marginTop: -4, marginBottom: 10, fontSize: 12 }}>
          Ajout manuel dans le Hub (sans toucher Lodgify). Les expériences réservées sur Lodgify seront récupérées automatiquement (à venir).
        </p>
        <div className="rg-grid-3">
          {EXTRA_KEYS.map((k) => {
            const on = (r.extras || []).includes(k);
            return (
              <div key={k} className="rg-card"
                data-testid={`rg-extra-${k}`}
                style={{ background: on ? "var(--rg-primary-light)" : "#fff", border: on ? "2px solid var(--rg-primary)" : "1px solid var(--rg-border)" }}>
                <button type="button" onClick={() => toggleExtra(k)} disabled={busy}
                  style={{ background: "transparent", border: 0, padding: 0, cursor: "pointer", textAlign: "left", width: "100%" }}>
                  <div style={{ fontSize: 20 }}>{EXTRA_ICONS[k]}</div>
                  <div style={{ fontWeight: 500, marginTop: 4 }}>{EXTRA_LABELS[k]}</div>
                  <div className="rg-tiny" style={{ marginTop: 4 }}>{on ? "Sélectionné" : "Non"}</div>
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mentions détectées */}
      {(r.mentioned_extras || []).length > 0 && (
        <div className="rg-section" data-testid="rg-mentioned-extras">
          <h3>💡 Mentions détectées</h3>
          <p className="rg-muted" style={{ marginTop: -4, marginBottom: 10, fontSize: 12 }}>
            Ces expériences ont été repérées dans les notes ou messages mais ne sont pas encore confirmées.
          </p>
          <div className="rg-grid-3">
            {(r.mentioned_extras || []).map((k) => (
              <div key={k} className="rg-card" data-testid={`rg-mentioned-${k}`}
                style={{ background: "#fff8e1", border: "2px dashed #f6c343", padding: 12 }}>
                <div style={{ fontSize: 20 }}>{EXTRA_ICONS[k]}<span style={{ opacity: 0.6 }}>❓</span></div>
                <div style={{ fontWeight: 500, marginTop: 4 }}>{EXTRA_LABELS[k]}</div>
                <div className="rg-tiny" style={{ marginTop: 4, color: "#8a6d1a" }}>Mentionné, non confirmé</div>
                <button type="button" onClick={() => toggleExtra(k)} disabled={busy}
                  className="rg-btn small" style={{ marginTop: 8, width: "100%" }}
                  data-testid={`rg-mentioned-add-${k}`}>+ Ajouter</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ====================== 6 — Envoyer un modèle (déplacé en bas) ====================== */}
      <div className="rg-section" data-testid="rg-block-templates">
        <h3>Envoyer un modèle</h3>
        <p className="rg-muted" style={{ marginTop: -4, marginBottom: 10, fontSize: 12 }}>
          Modèles Confirmation / Jour J / Merci — pré-remplit WhatsApp ou SMS (tu gardes la main sur l&apos;envoi).
        </p>
        <CommButtons reservation={r} templates={tpl} onLogSend={logAndSend} showContact={false} showTemplate />
      </div>

      {/* ====================== 7 — Préparation & courses (tout en bas) ====================== */}
      {activeModules.length > 0 && (
        <div className="rg-section" data-testid="rg-block-prep">
          <h3>Préparation &amp; courses</h3>
          <p className="rg-muted" style={{ marginTop: -4, marginBottom: 10, fontSize: 12 }}>
            Indépendant de l&apos;envoi des formulaires — recevoir une réponse ne coche aucune tâche.
          </p>
          {activeModules.map(({ key, modKey }) => {
            const prog = moduleProgress(r, modKey);
            return (
              <div key={key} className="rg-card" data-testid={`rg-prep-row-${modKey}`}
                style={{ padding: 12, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <span style={{ fontSize: 13 }}>
                  {EXTRA_ICONS[key]} {EXTRA_LABELS[key]}
                  <span className="rg-tiny" style={{ color: "var(--rg-text-3)" }}> · {prog.done}/{prog.total}</span>
                </span>
                <button type="button" className="rg-btn ghost small" onClick={() => setModuleOpen(modKey)} data-testid={`rg-open-module-${modKey}`}>
                  Ouvrir la préparation
                </button>
              </div>
            );
          })}
          <button className="rg-btn" style={{ marginTop: 4 }} onClick={() => setShoppingOpen(true)} data-testid="rg-open-shopping">
            <Package size={13} /> Voir la liste de courses consolidée
          </button>
        </div>
      )}

      {/* Modales */}
      <ModuleModal
        open={!!moduleOpen}
        onClose={() => setModuleOpen(null)}
        moduleKey={moduleOpen}
        reservation={r}
        onUpdateReservation={(patchData) => patch(patchData)}
      />
      <ShoppingListModal
        open={shoppingOpen}
        onClose={() => setShoppingOpen(false)}
        reservation={r}
      />
    </div>
  );
}
